import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

const CustomDropdown = ({ 
  options = [], 
  placeholder = "選択ください。", 
  onSelect, 
  ariaLabel,
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState("");
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // メニューの位置を計算
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, [isOpen]);

  // 外側クリックでドロップダウンを閉じる
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        // Portal内の要素もチェック
        const portalElements = document.querySelectorAll('.pull-down-menu-portal');
        let isClickInsidePortal = false;
        
        portalElements.forEach(portal => {
          if (portal.contains(event.target)) {
            isClickInsidePortal = true;
          }
        });
        
        if (!isClickInsidePortal) {
          setIsOpen(false);
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const toggleDropdown = (e) => {
    e.stopPropagation(); // イベントの伝播を停止
    setIsOpen(!isOpen);
  };

  const handleOptionSelect = (option) => {
    setSelectedLabel(option.label);
    
    // 確実にメニューを閉じる
    setTimeout(() => {
      setIsOpen(false);
    }, 0);
    
    if (onSelect) {
      onSelect(option);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleDropdown();
    } else if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const handleOptionKeyDown = (event, option) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleOptionSelect(option);
    }
  };

  // ドロップダウンメニューをbody直下に配置
  const dropdownMenu = isOpen ? createPortal(
    <ul 
      className="pull-down-menu-portal" 
      role="listbox"
      onClick={(e) => e.stopPropagation()} // クリックイベントの伝播を停止
      style={{
        position: 'absolute',
        top: menuPosition.top,
        left: menuPosition.left,
        width: menuPosition.width,
        backgroundColor: '#ffffff',
        border: '1px solid #dddddd',
        borderRadius: '0 0 4px 4px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        zIndex: 99999,
        maxHeight: '200px',
        overflowY: 'auto',
        listStyle: 'none',
        margin: 0,
        padding: 0
      }}
    >
      {options.map((option, index) => (
        <li 
          key={option.value || index}
          className="pull-down-menu-item-portal" 
          role="option"
          aria-selected={selectedLabel === option.label}
          tabIndex="0"
          onClick={(e) => {
            e.stopPropagation(); // クリックイベントの伝播を停止
            handleOptionSelect(option);
          }}
          onKeyDown={(e) => handleOptionKeyDown(e, option)}
          style={{
            backgroundColor: '#ffffff',
            borderBottom: index < options.length - 1 ? '1px solid #f0f0f0' : 'none',
            cursor: 'pointer',
            transition: 'background-color 0.2s ease'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#ffffff'}
        >
          <span 
            style={{
              display: 'block',
              padding: '12px 16px',
              color: '#333333',
              fontSize: '16px',
              fontFamily: '"Inter-Regular", Helvetica',
              pointerEvents: 'none' // spanはクリックイベントを受け取らない
            }}
          >
            {option.label}
          </span>
        </li>
      ))}
    </ul>,
    document.body
  ) : null;

  return (
    <div className={`custom-dropdown ${className}`} ref={dropdownRef}>
      <div className="pull-down-button">
        <button 
          ref={buttonRef}
          className="element-default" 
          aria-haspopup="listbox" 
          aria-expanded={isOpen}
          aria-label={ariaLabel}
          onClick={toggleDropdown}
          onKeyDown={handleKeyDown}
          type="button"
        >
          <span className={`text-wrapper ${selectedLabel ? 'selected' : 'placeholder'}`}>
            {selectedLabel || placeholder}
          </span>
          <img className={`arrow-down ${isOpen ? 'rotated' : ''}`} src="/images/home/arrow_down.svg" alt="" />
        </button>
        {dropdownMenu}
      </div>
    </div>
  );
};

export default CustomDropdown;